import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRight, Mail } from 'lucide-react';
import Masonry from 'react-masonry-css';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';
import Captions from 'yet-another-react-lightbox/plugins/captions';
import 'yet-another-react-lightbox/plugins/captions.css';
import { work, categories, shopShots, nasaShots } from '../../data/workData';
import { SocialIcons } from '../Footer';
import { setPageTitle } from '../utils';

const SectionHeading = ({ kicker, title }) => (
  <div className="mb-10">
    <p className="text-sm font-medium uppercase tracking-widest text-copper-600 dark:text-copper-400 mb-2">
      {kicker}
    </p>
    <h2 className="font-display text-3xl md:text-4xl font-semibold text-ink dark:text-bone">
      {title}
    </h2>
  </div>
);

const WorkCard = ({ project }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-40px' }}
    transition={{ duration: 0.35 }}
  >
    <Link to={`/work/${project.slug}`} className="group block">
      <div className="overflow-hidden rounded-xl border border-line-light dark:border-line-dark bg-cream dark:bg-charcoal aspect-[4/3]">
        <img
          src={`/images/${project.cover}`}
          alt={project.title}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
        />
      </div>
      <div className="pt-4">
        <div className="flex items-baseline justify-between gap-3">
          <h3 className="font-display text-xl font-semibold text-ink dark:text-bone group-hover:text-copper-600 dark:group-hover:text-copper-400 transition-colors">
            {project.title}
          </h3>
          <span className="shrink-0 text-xs font-medium uppercase tracking-wider text-muted-light dark:text-muted-dark">
            {project.category}
          </span>
        </div>
        <p className="mt-1 text-sm leading-relaxed text-muted-light dark:text-muted-dark">
          {project.blurb}
        </p>
      </div>
    </Link>
  </motion.div>
);

const PhotoWall = ({ shots }) => {
  const [index, setIndex] = useState(-1);

  const slides = shots.map((shot) => ({
    src: `/images/${shot.src}`,
    description: shot.caption,
  }));

  return (
    <>
      <Masonry
        breakpointCols={{ default: 4, 1100: 3, 768: 2 }}
        className="flex -ml-4 w-auto"
        columnClassName="pl-4 bg-clip-padding"
      >
        {shots.map((shot, i) => (
          <button
            key={shot.src}
            onClick={() => setIndex(i)}
            className="group relative block w-full mb-4 overflow-hidden rounded-lg border border-line-light dark:border-line-dark"
          >
            <img
              src={`/images/${shot.src}`}
              alt={shot.caption}
              loading="lazy"
              className="w-full transition-transform duration-500 group-hover:scale-[1.03]"
            />
            {shot.caption && (
              <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-3 pb-2 pt-8 text-left text-xs text-white opacity-0 group-hover:opacity-100 transition-opacity">
                {shot.caption}
              </span>
            )}
          </button>
        ))}
      </Masonry>
      <Lightbox
        open={index >= 0}
        close={() => setIndex(-1)}
        index={index}
        slides={slides}
        plugins={[Captions]}
      />
    </>
  );
};

const Home = () => {
  const location = useLocation();
  const [filter, setFilter] = useState('All');
  const [contactStatus, setContactStatus] = useState('');

  useEffect(() => {
    setPageTitle('Adam Gordon');
  }, []);

  // Support nav clicks from other pages (e.g. /work/:slug → "Contact")
  useEffect(() => {
    const anchor = location.state?.scrollTo;
    if (anchor) {
      setTimeout(() => {
        document.getElementById(anchor)?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [location.state]);

  const filtered = filter === 'All' ? work : work.filter((p) => p.category === filter);

  const submitForm = async (e) => {
    e.preventDefault();
    const form = e.target;
    const data = new FormData(form);
    const response = await fetch('https://formspree.io/f/xwpezovq', {
      method: 'POST',
      body: data,
      headers: { Accept: 'application/json' },
    });
    if (response.ok) {
      setContactStatus('Thanks — talk soon.');
      form.reset();
    } else {
      setContactStatus('Hmm, something went wrong. Email me directly instead?');
    }
  };

  return (
    <div>
      {/* Intro — hero and about, together */}
      <section className="max-w-site mx-auto px-5 pt-16 pb-20 md:pt-24 md:pb-28">
        <div className="grid md:grid-cols-5 gap-10 md:gap-16 items-center">
          <motion.div
            className="md:col-span-3"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="font-display text-5xl md:text-6xl font-semibold tracking-tight text-ink dark:text-bone mb-6">
              I make things.
            </h1>
            <div className="space-y-4 text-base md:text-lg leading-relaxed text-muted-light dark:text-muted-dark max-w-xl">
              <p>
                I'm Adam — a engineer  & maker who can't leave an idea
                unexplored. By day I work to steamline pharmaceutical manufacturing
                as an systems engineer at BMS; before that I worked at
                Prosper Robotics, Emerson, and NASA.
              </p>
              <p>
                The rest of the time I'm in the shop: tinkering with robots 
				and electronics, or making hardwood furniture, stained glass panels
				or leather goods. I love to try new things and gather more tools for my toolbelt. I film some of it as{' '}
                <a
                  href="https://www.youtube.com/@AdamGMakes"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-copper-600 dark:text-copper-400 hover:underline"
                >
                  AdamGMakes
                </a>
                .
              </p>
            </div>
            <div className="mt-8 flex items-center gap-6">
              <button
                onClick={() => document.getElementById('work')?.scrollIntoView({ behavior: 'smooth' })}
                className="inline-flex items-center gap-2 rounded-full bg-copper-600 px-6 py-2.5 text-sm font-semibold text-white hover:bg-copper-700 transition-colors"
              >
                See the work <ArrowUpRight size={16} />
              </button>
              <SocialIcons />
            </div>
          </motion.div>
          <motion.div
            className="md:col-span-2"
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.15 }}
          >
            <img
              src="/images/adam-1.jpg"
              alt="Adam Gordon"
              className="w-full rounded-2xl border border-line-light dark:border-line-dark object-cover"
            />
          </motion.div>
        </div>
      </section>

      {/* Work */}
      <section id="work" className="border-t border-line-light dark:border-line-dark scroll-mt-20">
        <div className="max-w-site mx-auto px-5 py-20">
          <SectionHeading kicker="Portfolio" title="Some past projects" />
          <div className="flex flex-wrap gap-2 mb-10">
            {['All', ...categories].map((cat) => (
              <button
                key={cat}
                onClick={() => setFilter(cat)}
                className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors border ${
                  filter === cat
                    ? 'bg-ink text-paper border-ink dark:bg-bone dark:text-coal dark:border-bone'
                    : 'border-line-light text-muted-light hover:border-copper-500 hover:text-copper-600 dark:border-line-dark dark:text-muted-dark dark:hover:text-copper-400'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12">
            {filtered.map((project) => (
              <WorkCard key={project.slug} project={project} />
            ))}
          </div>
        </div>
      </section>

      {/* gallery photo wall */}
      <section id="gallery" className="border-t border-line-light dark:border-line-dark scroll-mt-20">
        <div className="max-w-site mx-auto px-5 py-20">
          <SectionHeading kicker="Off the workbench" title="Some things I've made" />
          <p className="-mt-6 mb-10 max-w-xl text-muted-light dark:text-muted-dark">
            Smaller builds, gifts, experiments, and work that didn't warrant a
            whole project page deserved to be seen anyway.
          </p>
          <PhotoWall shots={shopShots} />
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="border-t border-line-light dark:border-line-dark scroll-mt-20">
        <div className="max-w-site mx-auto px-5 py-20">
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <SectionHeading kicker="Contact" title="Say hi" />
              <p className="-mt-4 max-w-md text-muted-light dark:text-muted-dark leading-relaxed">
                Questions about a build, want plans for something you saw, or
                just want to talk shop — drop a note here or email me at{' '}
                <a
                  href="mailto:adam@adamgmakes.com"
                  className="inline-flex items-center gap-1 font-medium text-copper-600 dark:text-copper-400 hover:underline"
                >
                  <Mail size={14} />
                  adam@adamgmakes.com
                </a>
                .
              </p>
              <SocialIcons className="mt-6" />
            </div>
            <form onSubmit={submitForm} className="space-y-4">
              <input
                type="text"
                name="name"
                placeholder="Name"
                required
                className="w-full rounded-lg border border-line-light bg-white p-3 text-ink placeholder:text-muted-light focus:border-copper-500 focus:outline-none dark:border-line-dark dark:bg-charcoal dark:text-bone dark:placeholder:text-muted-dark"
              />
              <input
                type="email"
                name="email"
                placeholder="Email"
                required
                className="w-full rounded-lg border border-line-light bg-white p-3 text-ink placeholder:text-muted-light focus:border-copper-500 focus:outline-none dark:border-line-dark dark:bg-charcoal dark:text-bone dark:placeholder:text-muted-dark"
              />
              <textarea
                name="message"
                placeholder="Message"
                rows="4"
                required
                className="w-full rounded-lg border border-line-light bg-white p-3 text-ink placeholder:text-muted-light focus:border-copper-500 focus:outline-none dark:border-line-dark dark:bg-charcoal dark:text-bone dark:placeholder:text-muted-dark"
              ></textarea>
              <button
                type="submit"
                className="rounded-full bg-copper-600 px-6 py-2.5 text-sm font-semibold text-white hover:bg-copper-700 transition-colors"
              >
                Send
              </button>
              {contactStatus && (
                <p className="text-sm text-ink dark:text-bone">{contactStatus}</p>
              )}
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
